import React, { useState } from 'react';
import {
  ChartBarIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  XCircleIcon,
  UserGroupIcon,
  ShareIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const DashboardSidebar = ({ activeSection, onSectionChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    {
      id: 'trend-charts',
      name: 'Tendances',
      icon: ChartBarIcon,
      description: 'Visualisation des tendances de commandes'
    },
    {
      id: 'regional-analysis',
      name: 'Analyse Régionale',
      icon: GlobeAltIcon,
      description: 'Performance par région'
    },
    {
      id: 'custom-reports',
      name: 'Rapports',
      icon: DocumentTextIcon,
      description: 'Rapports personnalisés'
    },
    {
      id: 'sales-forecast',
      name: 'Prévisions',
      icon: ArrowTrendingUpIcon,
      description: 'Prévisions des ventes'
    },
    {
      id: 'cancellation-analysis',
      name: 'Annulations',
      icon: XCircleIcon,
      description: 'Analyse des annulations'
    },
    {
      id: 'customer-segmentation',
      name: 'Segments Clients',
      icon: UserGroupIcon,
      description: 'Segmentation client'
    },
    {
      id: 'social-media',
      name: 'Réseaux Sociaux',
      icon: ShareIcon,
      description: 'Intégration réseaux sociaux'
    }
  ];

  const filteredSections = sections.filter(section =>
    section.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSectionClick = (sectionId) => {
    onSectionChange(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Menu mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-lg font-semibold text-gray-900">Dashboard</h2>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <Bars3Icon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
        
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200">
            <div className="p-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher une section..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            <nav className="px-4 pb-4 space-y-1">
              {filteredSections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    <span className="flex flex-col items-start">
                      <span className="font-medium">{section.name}</span>
                      <span className="text-xs text-gray-500">{section.description}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Sidebar desktop */}
      <div 
        className={`hidden lg:block fixed left-0 top-0 bottom-0 bg-white shadow-lg overflow-y-auto transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            {!isCollapsed && <h2 className="text-lg font-semibold text-gray-900">Dashboard</h2>}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>

          {!isCollapsed && (
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Rechercher une section..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          )}

          <nav className="space-y-1">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  title={isCollapsed ? `${section.name} - ${section.description}` : undefined}
                >
                  <Icon className="h-5 w-5 min-w-[1.25rem]" />
                  {!isCollapsed && (
                    <span className="flex flex-col items-start ml-3">
                      <span className="font-medium">{section.name}</span>
                      <span className="text-xs text-gray-500">{section.description}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
